import { useEffect, useRef, useState } from "react";
import type { MediaCreateInput, MediaDetail, MediaListItem, TmdbMediaType, TmdbSearchCandidate } from "@atlas/contracts";
import { ApiError, createMediaItem, searchTmdbMedia } from "../lib/api";
import { Sheet } from "./ui/Sheet";
import { Button } from "./ui/Button";
import { Field, Input, Textarea } from "./ui/Input";
import { ChipGroup } from "./ui/Select";
import { Alert } from "./ui/Alert";
import { LoadingState } from "./StateViews";

interface AddMediaSheetProps {
  open: boolean;
  onClose: () => void;
  /** Called with the created MediaDetail; the caller is responsible for
   * closing the sheet, refreshing the list, and navigating to the detail
   * page. */
  onCreated: (media: MediaDetail) => void;
}

type MediaStatus = MediaListItem["status"];
type Step = "search" | "details";
type SearchState = "idle" | "loading" | "error" | "empty" | "success";

const TYPE_OPTIONS: Array<{ label: string; value: TmdbMediaType }> = [
  { label: "فیلم", value: "movie" },
  { label: "سریال", value: "series" },
];

const STATUS_OPTIONS: Array<{ label: string; value: MediaStatus }> = [
  { label: "می‌خواهم ببینم", value: "want" },
  { label: "در حال تماشا", value: "watch" },
  { label: "تمام‌شده", value: "finished" },
  { label: "رهاشده", value: "dropped" },
];

function resetIdentityRef() {
  return crypto.randomUUID();
}

/** Mobile bottom-sheet flow for adding a movie/series from TMDB (Media FAB /
 * Quick Add → «افزودن فیلم یا سریال»): search TMDB, pick a result, then set
 * the user's own status/notes/rating/progress before creating. Canonical
 * metadata (title, cast, poster, ...) is never entered here -- it is always
 * refetched server-side from the selected TMDB identity. */
export function AddMediaSheet({ open, onClose, onCreated }: AddMediaSheetProps) {
  const [step, setStep] = useState<Step>("search");
  const [type, setType] = useState<TmdbMediaType>("movie");
  const [query, setQuery] = useState("");
  const [searchState, setSearchState] = useState<SearchState>("idle");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<TmdbSearchCandidate[]>([]);
  const [selected, setSelected] = useState<TmdbSearchCandidate | null>(null);

  const [status, setStatus] = useState<MediaStatus>("want");
  const [notes, setNotes] = useState("");
  const [rating, setRating] = useState("");
  const [season, setSeason] = useState("");
  const [episode, setEpisode] = useState("");
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const idempotencyKeyRef = useRef(resetIdentityRef());
  const submittedPayloadRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep("search");
    setType("movie");
    setQuery("");
    setSearchState("idle");
    setSearchError(null);
    setResults([]);
    setSelected(null);
    setStatus("want");
    setNotes("");
    setRating("");
    setSeason("");
    setEpisode("");
    setBusy(false);
    setCreateError(null);
    idempotencyKeyRef.current = resetIdentityRef();
    submittedPayloadRef.current = null;
  }, [open]);

  async function runSearch() {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearchState("loading");
    setSearchError(null);
    try {
      const res = await searchTmdbMedia(type, trimmed);
      setResults(res.results);
      setSearchState(res.results.length === 0 ? "empty" : "success");
    } catch (err) {
      setResults([]);
      setSearchState("error");
      setSearchError(err instanceof ApiError ? "جست‌وجو در TMDB با خطا مواجه شد." : "ارتباط با سرور برقرار نشد.");
    }
  }

  function selectCandidate(candidate: TmdbSearchCandidate) {
    setSelected(candidate);
    setCreateError(null);
    setStep("details");
  }

  function markPayloadDirty() {
    if (submittedPayloadRef.current !== null) {
      idempotencyKeyRef.current = resetIdentityRef();
      submittedPayloadRef.current = null;
    }
  }

  function buildPayload(): { ok: true; payload: MediaCreateInput } | { ok: false; error: string } {
    if (!selected) return { ok: false, error: "ابتدا یک نتیجه را انتخاب کنید." };
    const payload: MediaCreateInput = { provider: "tmdb", externalId: selected.externalId, type: selected.type, status };

    if (notes.trim()) payload.notes = notes.trim();

    if (rating.trim()) {
      const value = Number(rating.trim());
      if (!Number.isInteger(value) || value < 0 || value > 10) {
        return { ok: false, error: "امتیاز باید عددی صحیح بین ۰ تا ۱۰ باشد." };
      }
      payload.rating = value;
    }

    if (selected.type === "series") {
      if (season.trim()) {
        const value = Number(season.trim());
        if (!Number.isInteger(value) || value < 0) return { ok: false, error: "فصل باید عددی صحیح و نامنفی باشد." };
        payload.season = value;
      }
      if (episode.trim()) {
        const value = Number(episode.trim());
        if (!Number.isInteger(value) || value < 0) return { ok: false, error: "قسمت باید عددی صحیح و نامنفی باشد." };
        payload.episode = value;
      }
    }

    return { ok: true, payload };
  }

  async function submit() {
    if (busy) return;
    const result = buildPayload();
    if (!result.ok) {
      setCreateError(result.error);
      return;
    }

    const serialized = JSON.stringify(result.payload);
    if (submittedPayloadRef.current !== serialized) {
      idempotencyKeyRef.current = resetIdentityRef();
      submittedPayloadRef.current = serialized;
    }

    setBusy(true);
    setCreateError(null);
    try {
      const media = await createMediaItem(result.payload, idempotencyKeyRef.current);
      onCreated(media);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setCreateError("این عنوان قبلاً افزوده شده یا این درخواست قبلاً با اطلاعات متفاوتی ثبت شده است.");
      } else if (err instanceof ApiError && err.status === 503) {
        setCreateError("ارتباط با TMDB برقرار نشد. کمی بعد دوباره امتحان کنید.");
      } else {
        setCreateError("افزودن با خطا مواجه شد. اتصال یا اطلاعات وارد شده را بررسی کنید.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="افزودن فیلم یا سریال">
      {step === "search" && (
        <>
          <ChipGroup options={TYPE_OPTIONS} value={type} onChange={setType} ariaLabel="نوع" />
          <Field label="جست‌وجو" htmlFor="add-media-query">
            <Input
              id="add-media-query"
              type="search"
              role="searchbox"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void runSearch();
                }
              }}
              placeholder="نام فیلم یا سریال را وارد کنید"
            />
          </Field>
          <Button type="button" variant="secondary" onClick={runSearch} disabled={!query.trim() || searchState === "loading"}>
            جست‌وجو
          </Button>

          {searchState === "loading" && <LoadingState label="در حال جست‌وجو…" />}
          {searchState === "error" && (
            <Alert tone="danger" action={<Button variant="secondary" size="sm" onClick={runSearch}>تلاش دوباره</Button>}>
              {searchError}
            </Alert>
          )}
          {searchState === "empty" && <p className="state-block__hint">نتیجه‌ای پیدا نشد.</p>}
          {searchState === "success" && (
            <ul className="item-list">
              {results.map((candidate) => (
                <li key={candidate.externalId}>
                  <button type="button" className="item-row" onClick={() => selectCandidate(candidate)} style={{ width: "100%", textAlign: "right" }}>
                    {candidate.posterPreviewUrl ? (
                      <img src={candidate.posterPreviewUrl} alt={candidate.displayTitle} className="item-row__media item-row__media--media" />
                    ) : (
                      <span className="item-row__media item-row__media--media">بدون تصویر</span>
                    )}
                    <span className="item-row__body">
                      <span className="item-row__top">
                        <span className="item-row__title">{candidate.displayTitle}</span>
                      </span>
                      {candidate.originalTitle && candidate.originalTitle !== candidate.displayTitle && (
                        <span className="item-row__subtitle">{candidate.originalTitle}</span>
                      )}
                      {candidate.year && <span className="item-row__meta">{candidate.year}</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {step === "details" && selected && (
        <>
          {createError && <Alert tone="danger">{createError}</Alert>}

          <div className="info-card">
            <p className="detail-title">{selected.displayTitle}</p>
            {selected.originalTitle && selected.originalTitle !== selected.displayTitle && (
              <p className="detail-subtitle">{selected.originalTitle}</p>
            )}
          </div>

          <div className="field">
            <p className="field__label">وضعیت</p>
            <ChipGroup options={STATUS_OPTIONS} value={status} onChange={(v) => { setStatus(v); markPayloadDirty(); }} ariaLabel="وضعیت" />
          </div>

          <Field label="امتیاز شخصی (۰ تا ۱۰، اختیاری)" htmlFor="add-media-rating">
            <Input
              id="add-media-rating"
              type="number"
              dir="ltr"
              value={rating}
              onChange={(e) => { setRating(e.target.value); markPayloadDirty(); }}
              disabled={busy}
            />
          </Field>

          {selected.type === "series" && (
            <>
              <Field label="فصل" htmlFor="add-media-season">
                <Input
                  id="add-media-season"
                  type="number"
                  dir="ltr"
                  value={season}
                  onChange={(e) => { setSeason(e.target.value); markPayloadDirty(); }}
                  disabled={busy}
                />
              </Field>
              <Field label="قسمت" htmlFor="add-media-episode">
                <Input
                  id="add-media-episode"
                  type="number"
                  dir="ltr"
                  value={episode}
                  onChange={(e) => { setEpisode(e.target.value); markPayloadDirty(); }}
                  disabled={busy}
                />
              </Field>
            </>
          )}

          <Field label="یادداشت" htmlFor="add-media-notes">
            <Textarea
              id="add-media-notes"
              rows={3}
              value={notes}
              onChange={(e) => { setNotes(e.target.value); markPayloadDirty(); }}
              disabled={busy}
            />
          </Field>

          <div className="sheet-actions">
            <Button type="button" variant="secondary" onClick={() => setStep("search")} disabled={busy}>
              بازگشت
            </Button>
            <Button type="button" variant="primary" onClick={submit} disabled={busy}>
              افزودن
            </Button>
          </div>
        </>
      )}
    </Sheet>
  );
}
